import React, { useEffect, useState } from 'react';
import Card from '../components/Card';
import TabList from '../components/TabList';
import Button from '../components/Button';
import * as recApi from '../services/recommendationsApi';

const TYPE_TABS = [
  { key: '', label: 'All' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'reorder', label: 'Reorder' },
  { key: 'order', label: 'Order' },
  { key: 'dispatch', label: 'Dispatch' },
  { key: 'production', label: 'Production' },
];

function PriorityBadge({ priority }) {
  const colors = {
    critical: 'var(--color-danger)',
    high: '#E0855F',
    medium: 'var(--color-warning)',
    low: 'var(--color-text-muted)',
  };
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 700,
        color: '#fff',
        background: colors[priority] || 'var(--color-text-muted)',
        borderRadius: 4,
        padding: '2px 8px',
        textTransform: 'uppercase',
        flexShrink: 0,
      }}
    >
      {priority}
    </span>
  );
}

export default function RecommendationsPage() {
  const [type, setType] = useState('');
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastRun, setLastRun] = useState(null);

  const load = (t) => recApi.listRecommendations(t ? { recommendationType: t } : {}).then(setRecs);

  useEffect(() => {
    setLoading(true);
    load(type)
      .catch((err) => setError(err.message || 'Could not load recommendations.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const result = await recApi.refreshRecommendations();
      setLastRun(result);
      await load(type);
    } catch (err) {
      setError(err.message || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleAction = async (id, status) => {
    try {
      await recApi.setStatus(id, status);
      setRecs((prev) => prev.filter((r) => r.recommendation_id !== id));
    } catch (err) {
      setError(err.message || 'Could not update recommendation.');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>AI Recommendations</h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            A prioritized action inbox synthesized from Stock, Order, Dispatch, Production, and
            Demand signals.
          </p>
        </div>
        <Button variant="secondary" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? 'Analyzing…' : 'Refresh recommendations'}
        </Button>
      </div>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {lastRun && (
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
          {lastRun.inserted} active recommendation(s)
          {lastRun.suppressedByDismissCooldown > 0
            ? ` (${lastRun.suppressedByDismissCooldown} suppressed — recently dismissed)`
            : ''}.
        </p>
      )}

      <TabList tabs={TYPE_TABS} activeKey={type} onChange={setType} ariaLabel="Recommendation type" />

      {loading ? (
        <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {recs.map((r) => (
            <Card key={r.recommendation_id} style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <PriorityBadge priority={r.priority} />
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'capitalize' }}>
                      {r.recommendation_type}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>{r.message}</p>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <Button variant="secondary" onClick={() => handleAction(r.recommendation_id, 'accepted')}>
                    Accept
                  </Button>
                  <Button variant="text" onClick={() => handleAction(r.recommendation_id, 'dismissed')}>
                    Dismiss
                  </Button>
                </div>
              </div>
            </Card>
          ))}
          {recs.length === 0 && (
            <Card>
              <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
                No open recommendations in this category.
              </p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
